import React, { useState } from "react";

const WORKER_URL = "https://reflex-backend.alfaoir1.workers.dev";

const thing1List = [
  "Text your leads automatically",
  "Send follow-up emails for you",
  "Ask customers for reviews after every job",
  "Remind people about their appointments",
  "Chase unpaid quotes so you don't have to",
  "Bring old customers back for more work",
];

export default function RevivalSystem() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", website: "", note: "" });

  const openModal = () => {
    setIsModalOpen(true);
    setSubmitted(false);
    setForm({ name: "", phone: "", email: "", company: "", website: "", note: "" });
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      alert("Please fill in your name, phone, and email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${WORKER_URL}/revival-form`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, package: "Reflex Revival™", packagePrice: "$297/month" }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Unknown error");
      setSubmitted(true);
    } catch (err) {
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">

      {/* ── TOP VIDEO ── */}
      <div className="rev-top-vid-wrap">
  <video
    src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/videos/revival%20video%20banner.mp4"
    autoPlay
    muted
    loop
    playsInline
    preload="auto"
    className="rev-top-vid"
  />

  <style>{`
    .rev-top-vid-wrap {
      width: 100%;
      padding-top: 100px;
      box-sizing: border-box;
      background: #0a0a0a;
      overflow: hidden;
    }
    .rev-top-vid {
      width: 100%;
      display: block;
      height: 100vh;
      object-fit: cover;
      object-position: top;
      cursor: pointer;
    }
    @media (max-width: 720px) {
      .rev-top-vid-wrap {
        padding-top: 80px;
      }
      .rev-top-vid {
        height: 55vh;
      }
    }
  `}</style>
      </div>

      {/* ── HERO ── */}
      <section className="hero">
        <div className="wrap">
          <p className="eyebrow">Only 5 spots open this month</p>
          <h1>We Do 2 Things.<br /><span className="accent">Both Save You Money.</span></h1>
          <p className="hero-sub">No tech skills needed. No complicated setup. Read this in 60 seconds.</p>
          <button className="btn-primary" onClick={openModal}>Claim Your Spot — $297/month →</button>
          <p className="fine">No setup fee · No contract · 30-Day Money-Back Guarantee</p>
        </div>
      </section>

      {/* ── THING 1 ── */}
      <section className="section section-light">
        <div className="wrap">
          <div className="section-inner">
            <p className="tag">Thing #1</p>
            <h2>Your CRM Comes Pre-Loaded.<br />Everything Is Already Built For You.</h2>
            <p className="body-text">
              We hand you <strong>100+ ready-made automations</strong>. Already written. Already set up. Already working. You don't build a single thing.
            </p>
            <div className="callout">
              <span className="callout-icon">📱</span>
              <p>Think of it like buying a brand new phone — the apps are already there. You just turn it on and start using it.</p>
            </div>
            <p className="body-text">
              Most people who buy a CRM spend weeks trying to set it up — and give up. We skip all of that.
            </p>
            <ul className="checklist">
              {thing1List.map((item) => (
                <li key={item}>
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <path d="M2.5 8l3.5 3.5 6.5-7" stroke="#7b19bc" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── THING 2 ── */}
      <section className="section section-dim">
        <div className="wrap">
          <div className="section-inner">
            <p className="tag">Thing #2</p>
            <h2>We Follow Up Every Lead For You.<br />Using The CRM You Already Have.</h2>
            <div className="callout">
              <span className="callout-icon">🤝</span>
              <p>Think of it like hiring a receptionist who never sleeps — every lead gets a text, every quote gets a follow-up, without you lifting a finger.</p>
            </div>
            <p className="body-text">
              Already using GoHighLevel, Jobber, or HubSpot? Great — keep using it. You give us one thing: your <strong>API key</strong>. That's just a password you copy from your CRM settings — takes 30 seconds.
            </p>
            <div className="steps">
              {[
                { n: "1", strong: "Copy your API key.", rest: " It's in your CRM settings. Takes 30 seconds." },
                { n: "2", strong: "We plug in.", rest: " We connect to your CRM. Done in 48 hours." },
                { n: "3", strong: "Leads get followed up.", rest: " Texts and emails go out automatically. You just close the jobs." },
              ].map((s) => (
                <div key={s.n} className="step">
                  <span className="step-n">{s.n}</span>
                  <p><strong>{s.strong}</strong>{s.rest}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section section-dark">
        <div className="wrap cta-wrap">
          <p className="eyebrow eyebrow-light">Only 5 Spots Open This Month</p>
          <p className="cta-price">$297<span> / month</span></p>
          <p className="cta-fine">No setup fee · No contract · 30-Day Money-Back Guarantee</p>
          <button className="btn-primary" onClick={openModal}>Claim Your Spot →</button>
          <p className="cta-contact">Questions? Email us — we reply within 24 hours.</p>
        </div>
      </section>

      {/* ── MODAL ── */}
      {isModalOpen && (
        <div className="backdrop" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>✕</button>

            <div className="modal-left">
              <p className="mpkg">Reflex Revival™</p>
              <p className="mprice">$297<span>/mo</span></p>
              <p className="mguar">30-Day Money-Back</p>
              <div className="mrule" />
              <ul className="mlist">
                {[
                  "100+ Pre-Built Automations",
                  "Works with your existing CRM",
                  "SMS + Email Follow-Up",
                  "Estimate Recovery",
                  "Customer Reactivation",
                  "Review Automation",
                  "Website + SEO + Social — FREE",
                ].map((f) => (
                  <li key={f}>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                      <path d="M2 7l3 3 6-6" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            <div className="modal-right">
              {!submitted ? (
                <>
                  <h3 className="modal-title">Claim Your Spot</h3>
                  <p className="modal-sub">We'll get everything set up within 48 hours.</p>
                  <form className="form" onSubmit={handleSubmit}>
                    <div className="row2">
                      <div className="field">
                        <label>Full Name *</label>
                        <input type="text" name="name" required placeholder="Your full name" value={form.name} onChange={handleChange} />
                      </div>
                      <div className="field">
                        <label>Phone *</label>
                        <input type="tel" name="phone" required placeholder="Your phone number" value={form.phone} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="row2">
                      <div className="field">
                        <label>Email *</label>
                        <input type="email" name="email" required placeholder="you@company.com" value={form.email} onChange={handleChange} />
                      </div>
                      <div className="field">
                        <label>Company</label>
                        <input type="text" name="company" placeholder="Your company name" value={form.company} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="field">
                      <label>Current Website (if any)</label>
                      <input type="text" name="website" placeholder="https://yourwebsite.com" value={form.website} onChange={handleChange} />
                    </div>
                    <div className="field">
                      <label>Tell Us About Your Business</label>
                      <textarea name="note" rows={3} placeholder="What kind of jobs? What city? Goals?" value={form.note} onChange={handleChange} />
                    </div>
                    <button type="submit" className="btn-submit" disabled={submitting}>
                      {submitting ? "Submitting..." : "Submit & Proceed to Payment →"}
                    </button>
                  </form>
                </>
              ) : (
                <div className="confirm">
                  <div className="confirm-icon">✓</div>
                  <h3 className="modal-title">You're In!</h3>
                  <p className="confirm-body">Thanks, <strong>{form.name}</strong>! Your spot is reserved. We'll reach out within 24 hours to get everything set up.</p>
                  <div className="confirm-table">
                    <div><span>Package</span><span>Reflex Revival™</span></div>
                    <div><span>Investment</span><span style={{color:"#7b19bc"}}>$297/month</span></div>
                    <div><span>Guarantee</span><span>30-Day Money-Back</span></div>
                  </div>
                  <button className="btn-submit" onClick={(e) => { e.stopPropagation(); window.open("https://links.equiptbiz.com/payment-link/6a3c3a04390a6e280643a73a", "_blank"); }}>Pay $297 — Secure Checkout →</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

<style>{`
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .page {
    font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
    color: #0a0a0a;
    background: #ffffff;
  }

  .wrap {
    max-width: 720px;
    margin: 0 auto;
    padding: 0 32px;
  }

  /* ── HERO ── */
  .hero {
    background: #0a0a0a;
    padding: 100px 0 88px;
    text-align: center;
  }

  .eyebrow {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.4);
    margin-bottom: 24px;
  }
  .eyebrow-light { color: rgba(255,255,255,0.35); }

  h1 {
    font-size: clamp(34px, 5.5vw, 60px);
    font-weight: 800;
    letter-spacing: -0.04em;
    line-height: 1.07;
    color: #ffffff;
    margin-bottom: 20px;
  }
  .accent { color: #ffffff; }

  .hero-sub {
    font-size: 16px;
    color: rgba(255,255,255,0.45);
    line-height: 1.7;
    margin-bottom: 36px;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }

  .fine {
    font-size: 12px;
    color: rgba(255,255,255,0.25);
    margin-top: 14px;
    letter-spacing: 0.01em;
  }

  /* ── BUTTON ── */
  .btn-primary {
    display: inline-block;
    background: #7b19bc;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    padding: 14px 30px;
    border-radius: 100px;
    border: none;
    cursor: pointer;
    letter-spacing: -0.01em;
    box-shadow: 0 4px 0 #5a0e8f, 0 8px 28px rgba(123,25,188,0.3);
    transition: background 0.18s, transform 0.18s, box-shadow 0.18s;
  }
  .btn-primary:hover {
    background: #8e20d8;
    transform: translateY(-2px);
    box-shadow: 0 6px 0 #5a0e8f, 0 14px 36px rgba(123,25,188,0.4);
  }
  .btn-primary:active {
    transform: translateY(0);
    box-shadow: 0 2px 0 #5a0e8f;
  }

  /* ── SECTIONS ── */
  .section { padding: 80px 0; }
  .section-light { background: #ffffff; }
  .section-dim { background: #f9f9f9; }
  .section-dark {
    background: #0a0a0a;
  }

  .section-inner {
    max-width: 600px;
  }

  .tag {
    display: inline-block;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #888;
    border: 1px solid #e0e0e0;
    padding: 5px 12px;
    border-radius: 100px;
    margin-bottom: 20px;
  }

  h2 {
    font-size: clamp(24px, 3.5vw, 36px);
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.15;
    color: #0a0a0a;
    margin-bottom: 22px;
  }

  .body-text {
    font-size: 15.5px;
    color: #555;
    line-height: 1.75;
    margin-bottom: 18px;
  }
  .body-text strong { color: #0a0a0a; }

  .callout {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    background: #f5f5f5;
    border: 1px solid #e8e8e8;
    border-left: 2px solid #ccc;
    border-radius: 0 8px 8px 0;
    padding: 16px 20px;
    margin-bottom: 18px;
  }
  .callout-icon { font-size: 18px; flex-shrink: 0; line-height: 1.6; opacity: 0.75; }
  .callout p { font-size: 14.5px; color: #555; line-height: 1.65; }

  .checklist {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 6px;
  }
  .checklist li {
    display: flex;
    align-items: flex-start;
    gap: 11px;
    font-size: 15px;
    color: #333;
    line-height: 1.5;
  }
  .checklist svg { flex-shrink: 0; margin-top: 3px; }

  .steps {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 6px;
  }
  .step {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 10px;
    padding: 16px 20px;
  }
  .step-n {
    width: 24px; height: 24px;
    border-radius: 50%;
    background: #0a0a0a;
    color: #fff;
    font-size: 11px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
  }
  .step p { font-size: 15px; color: #444; line-height: 1.6; }
  .step strong { color: #0a0a0a; }

  /* ── CTA ── */
  .cta-wrap {
    text-align: center;
  }
  .cta-price {
    font-size: clamp(56px, 10vw, 88px);
    font-weight: 800;
    letter-spacing: -0.05em;
    color: #ffffff;
    line-height: 1;
    margin: 14px 0 10px;
  }
  .cta-price span {
    font-size: 20px;
    font-weight: 400;
    color: rgba(255,255,255,0.3);
    letter-spacing: 0;
  }
  .cta-fine {
    font-size: 12px;
    color: rgba(255,255,255,0.25);
    margin-bottom: 32px;
    letter-spacing: 0.01em;
  }
  .cta-contact {
    font-size: 13px;
    color: rgba(255,255,255,0.2);
    margin-top: 18px;
  }

  /* ── MODAL ── */
  .backdrop {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.55);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
    animation: fadein 0.18s ease;
  }
  @keyframes fadein { from { opacity:0 } to { opacity:1 } }

  .modal {
    position: relative;
    display: flex;
    width: 100%;
    max-width: 820px;
    max-height: 92vh;
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 32px 96px rgba(0,0,0,0.22);
    animation: scalein 0.22s cubic-bezier(0.34,1.3,0.64,1);
  }
  @keyframes scalein { from { transform:scale(0.95); opacity:0 } to { transform:scale(1); opacity:1 } }

  .modal-close {
    position: absolute; top: 14px; right: 14px;
    width: 30px; height: 30px; border-radius: 50%;
    background: rgba(0,0,0,0.07); border: none;
    font-size: 12px; color: #777; cursor: pointer;
    display: flex; align-items: center; justify-content: center; z-index: 5;
    transition: background 0.15s;
  }
  .modal-close:hover { background: rgba(0,0,0,0.13); color: #222; }

  .modal-left {
    width: 250px; flex-shrink: 0;
    background: #0a0a0a;
    padding: 40px 28px;
    display: flex; flex-direction: column;
    overflow-y: auto;
  }
  .mpkg {
    font-size: 9px; font-weight: 800; letter-spacing: 0.22em;
    text-transform: uppercase; color: rgba(255,255,255,0.25);
    margin-bottom: 12px;
  }
  .mprice {
    font-size: 50px; font-weight: 800; letter-spacing: -0.05em;
    color: #fff; line-height: 1; margin-bottom: 4px;
  }
  .mprice span { font-size: 15px; font-weight: 400; opacity: 0.3; }
  .mguar {
    font-size: 10px; color: rgba(255,255,255,0.25);
    text-transform: uppercase; letter-spacing: 0.1em;
    margin-bottom: 24px;
  }
  .mrule { height: 1px; background: rgba(255,255,255,0.08); margin-bottom: 22px; }
  .mlist {
    list-style: none;
    display: flex; flex-direction: column; gap: 11px; flex: 1;
  }
  .mlist li {
    display: flex; align-items: flex-start; gap: 10px;
    font-size: 12.5px; color: rgba(255,255,255,0.5); line-height: 1.45;
  }
  .mlist svg { flex-shrink: 0; margin-top: 2px; }

  .modal-right {
    flex: 1;
    padding: 40px 36px;
    overflow-y: auto;
    background: #fafafa;
  }
  .modal-title {
    font-size: 22px; font-weight: 800; letter-spacing: -0.022em;
    color: #0a0a0a; margin-bottom: 6px;
  }
  .modal-sub { font-size: 13px; color: #999; margin-bottom: 24px; }

  .form { display: flex; flex-direction: column; gap: 12px; }
  .row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .field { display: flex; flex-direction: column; gap: 5px; }
  .field label {
    font-size: 9.5px; font-weight: 800;
    letter-spacing: 0.14em; text-transform: uppercase; color: #aaa;
  }
  .field input, .field textarea {
    background: #fff; border: 1px solid #e0e0e0;
    border-radius: 8px; padding: 10px 12px;
    font-size: 13.5px; color: #0a0a0a;
    font-family: inherit; outline: none; width: 100%;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .field input::placeholder, .field textarea::placeholder { color: #ccc; }
  .field input:focus, .field textarea:focus {
    border-color: #7b19bc;
    box-shadow: 0 0 0 3px rgba(123,25,188,0.08);
    background: #fff;
  }
  .field textarea { resize: vertical; }

  .btn-submit {
    width: 100%; padding: 13px 20px;
    background: #7b19bc; color: #fff;
    font-size: 14px; font-weight: 700;
    border: none; border-radius: 9px; cursor: pointer;
    box-shadow: 0 4px 0 #5a0e8f;
    transition: background 0.18s, transform 0.18s;
    margin-top: 4px;
  }
  .btn-submit:hover { background: #8e20d8; transform: translateY(-1px); }
  .btn-submit:active { transform: translateY(0); }
  .btn-submit:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  /* CONFIRM */
  .confirm {
    display: flex; flex-direction: column; align-items: center;
    text-align: center; padding: 12px 0; gap: 16px;
  }
  .confirm-icon {
    width: 54px; height: 54px; border-radius: 50%;
    background: #f0fdf4; border: 1.5px solid #86efac;
    font-size: 20px; color: #16a34a;
    display: flex; align-items: center; justify-content: center;
  }
  .confirm-body { font-size: 14px; color: #555; line-height: 1.65; max-width: 320px; }
  .confirm-table {
    width: 100%; border: 1px solid #e8e8e8;
    border-radius: 8px; overflow: hidden; text-align: left;
  }
  .confirm-table > div {
    display: flex; justify-content: space-between;
    padding: 10px 16px; border-bottom: 1px solid #e8e8e8; font-size: 13px;
  }
  .confirm-table > div:last-child { border-bottom: none; }
  .confirm-table span:first-child {
    font-size: 9.5px; font-weight: 700;
    letter-spacing: 0.1em; text-transform: uppercase; color: #bbb;
  }
  .confirm-table span:last-child { font-weight: 600; color: #0a0a0a; }

  /* RESPONSIVE */
  @media (max-width: 600px) {
    .modal { flex-direction: column; max-height: 95vh; }
    .modal-left { width: 100%; padding: 24px 22px 16px; }
    .mlist { display: none; }
    .modal-right { padding: 24px 20px 28px; }
    .row2 { grid-template-columns: 1fr; }
    .mprice { font-size: 36px; }
  }
  @media (max-width: 420px) {
    .wrap { padding: 0 20px; }
    .btn-primary { width: 100%; text-align: center; }
    h1 { font-size: 30px; }
  }
`}</style>
    </div>
  );
}